import { useState } from 'react'
import { Modal } from '@/components/ui/modal'
import { useAddAdminUser } from '@/hooks/use-admin-users'

interface AddAdminModalProps {
  open: boolean
  onClose: () => void
}

export function AddAdminModal({ open, onClose }: AddAdminModalProps): React.JSX.Element {
  const addAdmin = useAddAdminUser()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<'admin' | 'viewer'>('admin')

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault()
    if (!email.trim()) return
    addAdmin.mutate(
      { email: email.trim(), name: name.trim() || undefined, role },
      {
        onSuccess: () => {
          setEmail('')
          setName('')
          setRole('admin')
          onClose()
        },
      },
    )
  }

  return (
    <Modal open={open} onClose={onClose} title="Add Admin User">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="add-admin-email" className="block text-sm font-medium text-text-primary">
            Email <span className="text-danger">*</span>
          </label>
          <input
            id="add-admin-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@example.com"
            required
            className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary"
          />
          <p className="mt-1 text-xs text-text-tertiary">
            An OTP invite email will be sent to this address.
          </p>
        </div>
        <div>
          <label htmlFor="add-admin-name" className="block text-sm font-medium text-text-primary">
            Name (optional)
          </label>
          <input
            id="add-admin-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe"
            className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary"
          />
        </div>
        <div>
          <label htmlFor="add-admin-role" className="block text-sm font-medium text-text-primary">
            Role
          </label>
          <select
            id="add-admin-role"
            value={role}
            onChange={(e) => setRole(e.target.value as 'admin' | 'viewer')}
            className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary"
          >
            <option value="admin">Admin (can manage domains, emails)</option>
            <option value="viewer">Viewer (read-only access)</option>
          </select>
          <p className="mt-1 text-xs text-text-tertiary">
            Super admin role cannot be assigned from the UI.
          </p>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={addAdmin.isPending || !email.trim()}
            className="rounded-lg bg-admin-500 px-4 py-2 text-sm font-medium text-white hover:bg-admin-600 disabled:opacity-50"
          >
            {addAdmin.isPending ? 'Adding...' : 'Add Admin'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
